import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PlusCircle, Trash, PencilSimple } from "@phosphor-icons/react";

const empty = { name: "", price: 0, daily_surf_limit: 100, credit_bonus: 0, referral_bonus_percent: 10, banner_ads: 100, text_ads: 0, featured: false, priority: 1, active: true, surf_timer_seconds: 0 };

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/admin/plans"); setPlans(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/admin/plans/${editing.id}`, form);
      else await api.post("/admin/plans", form);
      toast.success("Saved");
      setForm(empty); setEditing(null);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const edit = (p) => { setEditing(p); setForm({ ...empty, ...p }); };
  const del = async (p) => { if (!confirm(`Delete plan "${p.name}"?`)) return; await api.delete(`/admin/plans/${p.id}`); toast.success("Deleted"); load(); };

  return (
    <AdminLayout>
      <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white mb-5" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Membership Plans</h1>

      <form onSubmit={submit} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 mb-5 grid md:grid-cols-4 gap-3 text-sm">
        {[
          ["name", "Name", "text"], ["price", "Price ($)", "number"], ["daily_surf_limit", "Daily surfs", "number"],
          ["credit_bonus", "Credit bonus", "number"], ["referral_bonus_percent", "Referral %", "number"],
          ["banner_ads", "Banner ads", "number"], ["text_ads", "Text ads", "number"], ["priority", "Priority", "number"],
          ["surf_timer_seconds", "Surf timer (s) — 0 = default", "number"],
        ].map(([k, l, t]) => (
          <div key={k}>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">{l}</label>
            <input data-testid={`plan-${k}-input`} type={t} required value={form[k]} onChange={(e) => setForm({ ...form, [k]: t === "number" ? Number(e.target.value) : e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
          </div>
        ))}
        <label className="flex items-center gap-2"><input data-testid="plan-featured-check" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
        <label className="flex items-center gap-2"><input data-testid="plan-active-check" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
        <div className="md:col-span-4 flex gap-2">
          <button data-testid="plan-save-btn" className="rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold px-5 py-2 inline-flex items-center gap-1 transition-colors duration-200"><PlusCircle size={16} weight="fill" /> {editing ? "Update plan" : "Create plan"}</button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="rounded-full bg-slate-200 dark:bg-slate-700 px-4 py-2">Cancel</button>}
        </div>
      </form>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        {plans.map((p) => (
          <div key={p.id} data-testid={`admin-plan-${p.name}`} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
            <div className="flex items-center justify-between">
              <div className="font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{p.name}</div>
              <div className="flex gap-1">
                <button data-testid={`edit-plan-${p.name}`} onClick={() => edit(p)} className="h-7 w-7 rounded-full bg-slate-100 dark:bg-slate-800 inline-flex items-center justify-center text-blue-700"><PencilSimple size={14} weight="bold" /></button>
                <button data-testid={`del-plan-${p.name}`} onClick={() => del(p)} className="h-7 w-7 rounded-full bg-slate-100 dark:bg-slate-800 inline-flex items-center justify-center text-red-600"><Trash size={14} weight="bold" /></button>
              </div>
            </div>
            <div className="text-2xl font-black mt-1 text-slate-900 dark:text-white">${p.price}</div>
            <div className="text-xs text-slate-500 space-y-0.5 mt-2">
              <div>{p.daily_surf_limit} daily surfs</div>
              <div>{p.credit_bonus} bonus credits</div>
              <div>{p.referral_bonus_percent}% referral</div>
              <div>Surf timer: {p.surf_timer_seconds > 0 ? `${p.surf_timer_seconds}s` : "default"}</div>
              <div>{p.active ? "Active" : "Disabled"} {p.featured && "• Featured"}</div>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
